/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ServicesClientImpl } from '../../../core/impls';
import { BrandingUiInfoKeys } from '../../../core/entities/BrandingUiInfoKeys';
import { CellTypes } from './CellTypes';
import { ProgramCellProps } from './ProgramCellProps';
import { MINUTE_TO_PIXEL_FACTOR, PROGRAM_CELL_TRAILING_ICONS_WIDTH_OFFSET } from '../../../config';
import { GuideHighlight } from '../GuideHighlight';
import { Mousetrap } from '../../common';
import { DimensionScaler } from '../../../core/device/DimensionScaler';

import './ProgramCell.scss';

const ProgramCell: FunctionComponent<ProgramCellProps> = (props): JSX.Element => {
    const {
        duration,
        endTime,
        highlightId,
        isCatchup,
        isLeft,
        isNew,
        isPastGuideMode,
        isRight,
        isStartover,
        isSubscribed,
        movieYear,
        startTime,
        title,
        type,
        cuAvailabilityDuration,
        isRefreshed,
    } = props;

    const [isSocu, setIsSocu] = useState<boolean>(false);
    const [showNewIcon, setShowNewIcon] = useState<boolean>(!!isNew);
    const setSoStatusTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
    const setCuStatusTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
    const newIconSpanRef = useRef<HTMLSpanElement>(null);
    const socuIconSpanRef = useRef<HTMLSpanElement>(null);
    const programCellRef = useRef<HTMLDivElement>(null);

    const cellType = type || CellTypes.DEFAULT;
    const programCellTitle = title || '';
    const displayText = movieYear ? `${programCellTitle} (${movieYear})` : programCellTitle;
    const from = displayText.length - 4 < 0 ? 0 : displayText.length - 4;
    const trailingText = displayText.substr(from, 4) || '';
    let containerWidth = (duration || 0) * MINUTE_TO_PIXEL_FACTOR - 3;
    const isSafeLeft = containerWidth > 20 && isLeft;
    const isSafeRight = containerWidth > 20 && isRight;
    const programCellSpanRef = useRef<HTMLSpanElement>(null);
    const hasOverflow =
        (programCellSpanRef.current?.scrollWidth || 0) >
        (programCellSpanRef.current?.clientWidth || 0);
    let containerClasses = 'program-cell-container';
    const customStyle: any = {};
    const services = new ServicesClientImpl();
    const newIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.NEW_ICON);

    if (cellType === CellTypes.PAST_CONTINUOUS || cellType === CellTypes.FUTURE_CONTINUOUS) {
        containerClasses += ' continuous';
    }
    let programCellClasses = 'program-cell';
    if (!isLeft && cellType === CellTypes.PAST_CONTINUOUS) {
        programCellClasses += ' program-cell no-left-arrow';
    }

    if (isRight && cellType === CellTypes.FUTURE_CONTINUOUS) {
        programCellClasses += ' program-cell with-right-arrow';
    }

    const isNonTrailingOrLeading = !(
        cellType === CellTypes.TRAILING || cellType === CellTypes.LEADING
    );

    if (cellType === CellTypes.FUTURE_CONTINUOUS && !isPastGuideMode) {
        containerWidth += 6;
        customStyle.marginRight = 0;
    }
    customStyle.width = DimensionScaler.getInstance().getScaledWidth(containerWidth, false);
    let displayTextWidth = 'cell-text ';

    if (showNewIcon && isSocu) {
        displayTextWidth += `width-with-new-and-socu-icon ${newIcon?.imageType}`;
    } else if (showNewIcon) {
        displayTextWidth += `width-with-new-icon ${newIcon?.imageType}`;
    } else if (isSocu) {
        displayTextWidth += 'width-with-socu-icon';
    } else {
        displayTextWidth += 'width-without-icon';
    }

    const disableCatchupAfterCatchupDuration = () => {
        if (cuAvailabilityDuration && endTime) {
            const timeToDisableCu = cuAvailabilityDuration - (Date.now() - endTime.getTime());
            setCuStatusTimeoutId.current = setTimeout(() => {
                setIsSocu(false);
            }, timeToDisableCu);
        }
    };

    const validateAndSetCatchupStatus = () => {
        if (
            cuAvailabilityDuration &&
            endTime &&
            Date.now() - endTime.getTime() <= cuAvailabilityDuration
        ) {
            setIsSocu(true);
            disableCatchupAfterCatchupDuration();
        } else {
            setIsSocu(false);
        }
    };

    const checkStartoverAndSetStatus = () => {
        if (isStartover && startTime && endTime) {
            const currentTime = Date.now();
            const isCurrentlyAiring =
                startTime.getTime() <= currentTime && currentTime < endTime.getTime();
            const willAirInFuture = currentTime < startTime.getTime();
            if (isCurrentlyAiring) setIsSocu(true);
            else if (willAirInFuture) {
                const timeToEnableStartover = startTime.getTime() - currentTime;
                setSoStatusTimeoutId.current = setTimeout(() => {
                    setIsSocu(true);
                }, timeToEnableStartover);
            }
        }
    };

    const checkCatchupAndSetStatus = () => {
        if (isCatchup && endTime && startTime) {
            const currentTime = Date.now();
            const isPastProgram = endTime.getTime() <= currentTime;
            const willAirInFuture = currentTime < startTime.getTime();
            if (isPastProgram) {
                validateAndSetCatchupStatus();
            } else if (willAirInFuture) {
                const timeToEnableCu = endTime.getTime() - currentTime;
                setCuStatusTimeoutId.current = setTimeout(() => {
                    validateAndSetCatchupStatus();
                }, timeToEnableCu);
            }
        }
    };

    const clearTimeouts = () => {
        if (setSoStatusTimeoutId.current) clearTimeout(setSoStatusTimeoutId.current);
        if (setCuStatusTimeoutId.current) clearTimeout(setCuStatusTimeoutId.current);
    };

    useEffect(() => {
        if (isRefreshed) {
            clearTimeouts();
            checkStartoverAndSetStatus();
            checkCatchupAndSetStatus();
        }
    }, [isRefreshed]);

    useEffect(() => {
        if (isSocu)
            setShowNewIcon((showIcon) => {
                return (
                    showIcon &&
                    (socuIconSpanRef.current?.offsetWidth || 0) +
                        (newIconSpanRef.current?.offsetWidth || 0) +
                        PROGRAM_CELL_TRAILING_ICONS_WIDTH_OFFSET <
                        (programCellRef.current?.offsetWidth || 0)
                );
            });
        else {
            setShowNewIcon(!!isNew);
        }
    }, [isSocu]);

    useEffect(() => {
        checkStartoverAndSetStatus();
        checkCatchupAndSetStatus();
        return () => {
            clearTimeouts();
        };
    }, []);

    return (
        <React.Fragment>
            {isNonTrailingOrLeading ? (
                <Mousetrap
                    className={containerClasses}
                    value={highlightId || ''}
                    style={customStyle}
                >
                    <GuideHighlight id={highlightId} cellType={cellType}></GuideHighlight>
                    {isSafeLeft ? <span className="left-pointer"></span> : null}
                    <div ref={programCellRef} className={programCellClasses}>
                        <span
                            ref={programCellSpanRef}
                            className={`
                                ${displayTextWidth}
                                ${!isSubscribed ? 'cell-fadeout' : ''}
                                ${hasOverflow ? 'cell-fade-effect' : ''}
                            `}
                        >
                            {displayText}
                        </span>
                        {showNewIcon ? (
                            <img
                                src={newIcon?.imageUrl}
                                className={`icon-new ${newIcon?.imageType}`}
                                data-testid={'program-cell-new-icon'}
                            />
                        ) : null}
                        {isSocu ? <span ref={socuIconSpanRef} className="icon-socu"></span> : null}
                    </div>
                    {isSafeRight ? <span className="right-pointer"></span> : null}
                </Mousetrap>
            ) : null}

            {cellType === CellTypes.TRAILING ? (
                <div className="program-cell-trailing-container">
                    <div className="program-cell">
                        <span className="cell-text text-right">{trailingText}</span>
                    </div>
                </div>
            ) : null}

            {cellType === CellTypes.LEADING ? (
                <div className="program-cell-leading-container">
                    <div className="program-cell">
                        <span className="cell-text text-left">{displayText}</span>
                    </div>
                </div>
            ) : null}
        </React.Fragment>
    );
};

ProgramCell.propTypes = {
    cuAvailabilityDuration: PropTypes.number,
    duration: PropTypes.number,
    endTime: PropTypes.instanceOf(Date),
    highlightId: PropTypes.string,
    isCatchup: PropTypes.bool,
    isLeft: PropTypes.bool,
    isNew: PropTypes.bool,
    isPastGuideMode: PropTypes.bool,
    isRight: PropTypes.bool,
    isStartover: PropTypes.bool,
    isSubscribed: PropTypes.bool,
    movieYear: PropTypes.number,
    startTime: PropTypes.instanceOf(Date),
    title: PropTypes.string,
    type: PropTypes.string,
    isRefreshed: PropTypes.bool,
};

export default ProgramCell;
